// ============================================================================
// Rascunho da tela "Novo contrato".
//
// O estado da tela vivia só em `useState`: F5, fechar a aba ou trocar de
// máquina apagava a conversa, os dados preenchidos e matava o acompanhamento
// da geração em curso. Aqui ele passa a viver em `contrato_rascunhos` (RLS por
// dono), com duas regras de higiene que evitam o problema clássico de rascunho
// salvo — dado velho ressurgindo sem o usuário pedir:
//
//   1. Restauração NUNCA é silenciosa: a tela pergunta antes de retomar.
//   2. Rascunho velho (ou de um formato antigo) não volta: expira.
// ============================================================================

import { supabase } from "./supabase";
import type { DadosContrato } from "./contratoSchema";
import type { ChatMessage } from "./types";

/** Sobe quando o formato de `dados`/`messages` mudar: rascunho antigo é ignorado. */
export const SCHEMA_VERSAO = 1;
const VALIDADE_DIAS = 7;

export type StatusRascunho = "rascunho" | "gerando" | "concluido" | "descartado";

export interface Rascunho {
  id: string;
  handoff_id: string | null;
  modo: "form" | "chat";
  titulo: string | null;
  messages: ChatMessage[];
  dados: DadosContrato;
  status: StatusRascunho;
  doc_ref: string | null;
  disparado_em: string | null;
  contrato_id: string | null;
  cliente_id: string | null;
  schema_versao: number;
  created_at: string;
  updated_at: string;
}

const COLUNAS =
  "id, handoff_id, modo, titulo, messages, dados, status, doc_ref, disparado_em, contrato_id, cliente_id, schema_versao, created_at, updated_at";

/** Nome amigável para a faixa de retomada ("Retomar Núcleo Ser Plural"). */
export function tituloDoRascunho(dados: DadosContrato): string {
  return (
    dados.nome_projeto?.trim() ||
    dados.nome_empresarial?.trim() ||
    dados.nome_completo?.trim() ||
    "Contrato sem nome"
  ).slice(0, 120);
}

/** CNPJ (PJ) ou CPF (PF) — é a chave com que o status confirma o contrato. */
export function docDoContrato(dados: DadosContrato): string {
  const cnpj = String(dados.cnpj ?? "").replace(/\D/g, "");
  if (cnpj.length === 14) return cnpj;
  return String(dados.cpf ?? "").replace(/\D/g, "");
}

function vazio(dados: DadosContrato, messages: ChatMessage[]): boolean {
  const comValor = Object.values(dados ?? {}).some((v) => {
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== null && String(v).trim() !== "" && String(v) !== "-";
  });
  return !comValor && (messages?.length ?? 0) === 0;
}

/**
 * Rascunho ativo do usuário (o mais recente). Ignora o que expirou ou foi
 * gravado num formato anterior — deixar isso voltar é o que quebra a tela.
 */
export async function buscarAtivo(userId: string): Promise<Rascunho | null> {
  const limite = new Date(Date.now() - VALIDADE_DIAS * 86400_000).toISOString();
  const { data, error } = await supabase
    .from("contrato_rascunhos")
    .select(COLUNAS)
    .eq("user_id", userId)
    .in("status", ["rascunho", "gerando"])
    .eq("schema_versao", SCHEMA_VERSAO)
    .gte("updated_at", limite)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return data as unknown as Rascunho;
}

export interface PatchRascunho {
  modo?: "form" | "chat";
  messages?: ChatMessage[];
  dados?: DadosContrato;
  status?: StatusRascunho;
  doc_ref?: string | null;
  disparado_em?: string | null;
  contrato_id?: string | null;
  cliente_id?: string | null;
  handoff_id?: string | null;
}

/**
 * Cria ou atualiza o rascunho. Devolve o id (o chamador guarda para os
 * próximos saves). Falha de rede aqui é silenciosa de propósito: perder o
 * autosave não pode interromper quem está preenchendo o contrato.
 */
export async function salvar(
  userId: string,
  id: string | null,
  patch: PatchRascunho
): Promise<string | null> {
  try {
    if (patch.dados && patch.messages && vazio(patch.dados, patch.messages) && !id) {
      return null; // nada preenchido ainda: não cria rascunho fantasma
    }

    const linha: Record<string, unknown> = { ...patch };
    if (patch.dados) {
      linha.titulo = tituloDoRascunho(patch.dados);
      linha.doc_ref = patch.doc_ref ?? (docDoContrato(patch.dados) || null);
    }

    if (id) {
      const { error } = await supabase.from("contrato_rascunhos").update(linha).eq("id", id);
      return error ? null : id;
    }

    const { data, error } = await supabase
      .from("contrato_rascunhos")
      .insert({ ...linha, user_id: userId, schema_versao: SCHEMA_VERSAO })
      .select("id")
      .single();
    return error ? null : ((data?.id as string) ?? null);
  } catch {
    return null;
  }
}

export async function descartar(id: string): Promise<void> {
  try {
    await supabase.from("contrato_rascunhos").update({ status: "descartado" }).eq("id", id);
  } catch { /* não bloqueia a tela */ }
}

/** Marca que a geração foi disparada — é o que permite retomar após um F5. */
export async function marcarGerando(
  userId: string,
  id: string | null,
  dados: DadosContrato,
  messages: ChatMessage[],
  modo: "form" | "chat"
): Promise<string | null> {
  return salvar(userId, id, {
    modo,
    dados,
    messages,
    status: "gerando",
    doc_ref: docDoContrato(dados) || null,
    disparado_em: new Date().toISOString(),
  });
}

export async function marcarConcluido(
  id: string,
  ids: { contrato_id?: string | null; cliente_id?: string | null }
): Promise<void> {
  try {
    await supabase
      .from("contrato_rascunhos")
      .update({
        status: "concluido",
        contrato_id: ids.contrato_id ?? null,
        cliente_id: ids.cliente_id ?? null,
      })
      .eq("id", id);
  } catch { /* não bloqueia a tela */ }
}

/** Contratos já gerados por este usuário, com a conversa que os originou. */
export async function listarHistorico(userId: string, limite = 30): Promise<Rascunho[]> {
  const { data, error } = await supabase
    .from("contrato_rascunhos")
    .select(COLUNAS)
    .eq("user_id", userId)
    .eq("status", "concluido")
    .order("created_at", { ascending: false })
    .limit(limite);
  if (error || !data) return [];
  return data as unknown as Rascunho[];
}

/** Limpeza oportunista: descartados e expirados somem no carregamento da tela. */
export async function limparAntigos(userId: string): Promise<void> {
  try {
    const limite = new Date(Date.now() - VALIDADE_DIAS * 86400_000).toISOString();
    await supabase
      .from("contrato_rascunhos")
      .delete()
      .eq("user_id", userId)
      .in("status", ["rascunho", "descartado"])
      .lt("updated_at", limite);
  } catch { /* silencioso */ }
}

/**
 * Só os dados cadastrais do cliente — plano, valor, vigência e datas ficam de
 * fora de propósito: são decisão comercial de cada contrato, e reaproveitá-las
 * é o jeito mais fácil de gerar um contrato errado.
 */
export function dadosCadastraisDe(dados: DadosContrato): DadosContrato {
  const manter: (keyof DadosContrato)[] = [
    "nome_empresarial", "cnpj", "nome_completo", "cpf", "rg", "profissao",
    "estado_civil", "nacionalidade", "email_assinatura", "telefone",
    "endereco", "numero", "complemento", "bairro", "cidade", "estado", "cep",
    // Nome do projeto é do cliente, não do contrato — vale reaproveitar.
    // As transcrições NÃO: cada contrato tem a reunião de fechamento dele.
    "nome_projeto",
  ];
  const saida: DadosContrato = {};
  for (const k of manter) {
    const v = dados[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      (saida as Record<string, unknown>)[k] = v;
    }
  }
  return saida;
}
